import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Plus, Clock, MapPin, User, CalendarDays } from 'lucide-react';

type ViewMode = 'month' | 'week' | 'day';

interface CalendarEvent {
  id: number;
  title: string;
  description?: string | null;
  start_time: string;
  end_time: string;
  location?: string;
  color?: string;
  child_id?: string | null;
  child_name?: string | null;
  recurring?: boolean;
}

interface CalendarGridProps {
  events: CalendarEvent[];
  onDayClick: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
  onCreateEvent: () => void;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKDAYS_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

function formatTimeRange(start: string, end: string): string {
  return `${formatTime(start)} – ${formatTime(end)}`;
}

function getMonthDays(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startOffset = firstDay.getDay();
  const days: Date[] = [];

  for (let i = startOffset - 1; i >= 0; i--) {
    days.push(new Date(year, month, -i));
  }

  for (let d = 1; d <= lastDay.getDate(); d++) {
    days.push(new Date(year, month, d));
  }

  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    days.push(new Date(year, month + 1, i));
  }

  return days;
}

function getWeekDays(date: Date): Date[] {
  const start = new Date(date);
  start.setDate(start.getDate() - start.getDay());
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    days.push(new Date(start.getFullYear(), start.getMonth(), start.getDate() + i));
  }
  return days;
}

function getEventsForDay(events: CalendarEvent[], date: Date): CalendarEvent[] {
  return events.filter((e) => {
    const eventDate = new Date(e.start_time);
    return isSameDay(eventDate, date);
  });
}

const DEFAULT_EVENT_COLOR = '#4f46e5';

// Event pill component
const EventPill: React.FC<{
  event: CalendarEvent;
  compact?: boolean;
  onClick: (e: React.MouseEvent) => void;
}> = ({ event, compact, onClick }) => {
  const bgColor = event.color || DEFAULT_EVENT_COLOR;

  return (
    <button
      onClick={onClick}
      className="w-full text-left group/pill"
      title={event.title}
    >
      <div
        className={`flex items-center gap-1.5 rounded-lg transition-all duration-200
          hover:shadow-soft-sm hover:scale-[1.02] active:scale-[0.98] ${
          compact ? 'px-1.5 py-[3px]' : 'px-2 py-1'
        }`}
        style={{ backgroundColor: `${bgColor}18`, borderLeft: `3px solid ${bgColor}` }}
      >
        <span
          className={`truncate font-semibold ${compact ? 'text-2xs' : 'text-xs'}`}
          style={{ color: bgColor }}
        >
          {event.title}
        </span>
        {!compact && (
          <span className="text-2xs text-muted-foreground/70 ml-auto flex-shrink-0 hidden group-hover/pill:inline">
            {formatTime(event.start_time)}
          </span>
        )}
      </div>
    </button>
  );
};

// Event detail popover
const EventPopover: React.FC<{
  event: CalendarEvent;
  onEdit: () => void;
}> = ({ event, onEdit }) => (
  <motion.div
    initial={{ opacity: 0, y: 6, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: 6, scale: 0.95 }}
    transition={{ duration: 0.18, ease: [0.23, 1, 0.32, 1] }}
    className="absolute z-50 mt-1 left-0 right-0 min-w-[220px] bg-card/95 backdrop-blur-lg border border-border/80 rounded-xl shadow-soft-xl p-3.5"
    onClick={(e) => e.stopPropagation()}
  >
    <div className="flex items-start gap-2.5">
      <div
        className="w-3 h-3 rounded-md mt-0.5 flex-shrink-0 ring-2 ring-offset-1 ring-offset-card"
        style={{ backgroundColor: event.color || DEFAULT_EVENT_COLOR }}
      />
      <div className="flex-1 min-w-0">
        <h4 className="font-bold text-sm text-card-foreground m-0 truncate">{event.title}</h4>
        <div className="flex items-center gap-1.5 mt-1.5 text-xs text-muted-foreground">
          <Clock className="w-3 h-3 text-muted-foreground/60" />
          <span>{formatTimeRange(event.start_time, event.end_time)}</span>
        </div>
        {event.location && (
          <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
            <MapPin className="w-3 h-3 text-muted-foreground/60" />
            <span className="truncate">{event.location}</span>
          </div>
        )}
        {event.child_name && (
          <div className="flex items-center gap-1.5 mt-1 text-xs text-muted-foreground">
            <User className="w-3 h-3 text-muted-foreground/60" />
            <span>{event.child_name}</span>
          </div>
        )}
        {event.description && (
          <p className="text-xs text-muted-foreground/80 mt-2 line-clamp-2 m-0 leading-relaxed">{event.description}</p>
        )}
      </div>
    </div>
    <div className="mt-3 pt-2.5 border-t border-border/60">
      <button
        onClick={onEdit}
        className="w-full text-xs font-semibold text-primary hover:text-primary-hover transition-colors text-center py-1.5 rounded-lg hover:bg-primary/5 active:scale-[0.98]"
      >
        View Details
      </button>
    </div>
  </motion.div>
);

// Day cell for month view
const DayCell: React.FC<{
  date: Date;
  currentMonth: number;
  events: CalendarEvent[];
  onDayClick: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
}> = ({ date, currentMonth, events, onDayClick, onEventClick }) => {
  const [hoveredEvent, setHoveredEvent] = useState<CalendarEvent | null>(null);
  const isCurrentMonth = date.getMonth() === currentMonth;
  const today = isToday(date);
  const dayEvents = getEventsForDay(events, date);
  const maxVisible = 3;
  const overflow = dayEvents.length - maxVisible;

  return (
    <div
      className={`relative min-h-[110px] border-b border-r border-border/60 p-1.5 cursor-pointer
        transition-all duration-200 group/cell
        ${isCurrentMonth
          ? 'bg-card hover:bg-primary/[0.02] dark:hover:bg-primary/[0.04]'
          : 'bg-muted/20 dark:bg-muted/10 hover:bg-muted/40'
        }
        ${today ? 'ring-2 ring-inset ring-primary/20 bg-primary/[0.02] dark:bg-primary/[0.05]' : ''}
      `}
      onClick={() => onDayClick(date)}
    >
      {/* Day number */}
      <div className="flex items-center justify-between mb-1.5">
        <span
          className={`inline-flex items-center justify-center w-7 h-7 text-xs font-semibold rounded-lg transition-all duration-200
            ${today
              ? 'bg-primary text-primary-foreground shadow-soft-sm font-bold'
              : ''
            }
            ${!today && isCurrentMonth ? 'text-foreground hover:bg-muted/60' : ''}
            ${!today && !isCurrentMonth ? 'text-muted-foreground/40' : ''}
          `}
        >
          {date.getDate()}
        </span>
        <button
          className="w-5 h-5 rounded-md flex items-center justify-center text-muted-foreground/30 hover:text-primary hover:bg-primary/10 transition-all opacity-0 group-hover/cell:opacity-100 active:scale-90"
          onClick={(e) => {
            e.stopPropagation();
            onDayClick(date);
          }}
          aria-label={`Add event on ${date.toLocaleDateString()}`}
        >
          <Plus className="w-3 h-3" />
        </button>
      </div>

      {/* Event pills */}
      <div className="space-y-0.5">
        {dayEvents.slice(0, maxVisible).map((event) => (
          <div key={event.id} className="relative"
            onMouseEnter={() => setHoveredEvent(event)}
            onMouseLeave={() => setHoveredEvent(null)}
          >
            <EventPill
              event={event}
              compact
              onClick={(e) => {
                e.stopPropagation();
                onEventClick(event);
              }}
            />
            <AnimatePresence>
              {hoveredEvent?.id === event.id && (
                <EventPopover
                  event={event}
                  onEdit={() => onEventClick(event)}
                />
              )}
            </AnimatePresence>
          </div>
        ))}
        {overflow > 0 && (
          <span className="text-2xs text-muted-foreground/70 pl-1.5 font-semibold tracking-tight">
            +{overflow} more
          </span>
        )}
      </div>
    </div>
  );
};

// Week view day column
const WeekDayColumn: React.FC<{
  date: Date;
  events: CalendarEvent[];
  onDayClick: (date: Date) => void;
  onEventClick: (event: CalendarEvent) => void;
}> = ({ date, events, onDayClick, onEventClick }) => {
  const today = isToday(date);
  const dayEvents = getEventsForDay(events, date);

  return (
    <div
      className={`flex-1 min-w-0 border-r border-border/60 last:border-r-0 transition-colors ${
        today ? 'bg-primary/[0.02] dark:bg-primary/[0.04]' : ''
      }`}
    >
      {/* Day header */}
      <div className={`text-center py-3.5 border-b border-border/60 ${today ? 'bg-primary/[0.04] dark:bg-primary/[0.06]' : 'bg-muted/30 dark:bg-muted/20'}`}>
        <div className="text-2xs text-muted-foreground font-semibold uppercase tracking-widest">{WEEKDAYS[date.getDay()]}</div>
        <div
          className={`text-lg font-bold mt-1 ${
            today
              ? 'w-9 h-9 rounded-xl bg-primary text-primary-foreground inline-flex items-center justify-center mx-auto shadow-soft-sm'
              : 'text-foreground'
          }`}
        >
          {date.getDate()}
        </div>
      </div>

      {/* Events */}
      <div
        className="p-2.5 space-y-2 min-h-[320px] cursor-pointer hover:bg-muted/20 dark:hover:bg-muted/10 transition-colors"
        onClick={() => onDayClick(date)}
      >
        {dayEvents.map((event) => (
          <motion.div
            key={event.id}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-xl p-2.5 cursor-pointer transition-all duration-200 hover:shadow-soft-sm active:scale-[0.98] border border-transparent hover:border-border/50"
            style={{ backgroundColor: `${event.color || DEFAULT_EVENT_COLOR}10` }}
            onClick={(e) => {
              e.stopPropagation();
              onEventClick(event);
            }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span
                className="w-2 h-2 rounded-full flex-shrink-0 ring-1 ring-offset-1 ring-offset-card"
                style={{
                  backgroundColor: event.color || DEFAULT_EVENT_COLOR,
                }}
              />
              <span className="text-xs font-bold truncate text-foreground">{event.title}</span>
            </div>
            <div className="text-2xs text-muted-foreground pl-3.5 flex items-center gap-1">
              <Clock className="w-2.5 h-2.5" />
              {formatTimeRange(event.start_time, event.end_time)}
            </div>
            {event.location && (
              <div className="flex items-center gap-1 text-2xs text-muted-foreground pl-3.5 mt-0.5">
                <MapPin className="w-2.5 h-2.5" />
                <span className="truncate">{event.location}</span>
              </div>
            )}
            {event.child_name && (
              <div className="mt-1.5 pl-3.5">
                <span
                  className="inline-block px-2 py-0.5 rounded-md text-2xs font-semibold"
                  style={{
                    backgroundColor: `${event.color || DEFAULT_EVENT_COLOR}15`,
                    color: event.color || DEFAULT_EVENT_COLOR,
                  }}
                >
                  {event.child_name}
                </span>
              </div>
            )}
          </motion.div>
        ))}
        {dayEvents.length === 0 && (
          <div className="flex items-center justify-center h-16 opacity-0 hover:opacity-100 transition-opacity">
            <div className="flex flex-col items-center gap-1">
              <Plus className="w-4 h-4 text-muted-foreground/30" />
              <span className="text-2xs text-muted-foreground/30">Add event</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Day view - single day with time slots
const DayView: React.FC<{
  date: Date;
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
  onTimeSlotClick: (date: Date) => void;
}> = ({ date, events, onEventClick, onTimeSlotClick }) => {
  const dayEvents = getEventsForDay(events, date);
  const hours = Array.from({ length: 24 }, (_, i) => i);
  const currentHour = new Date().getHours();
  const showNowLine = isToday(date);

  return (
    <div className="border border-border/60 rounded-2xl overflow-hidden bg-card shadow-soft-sm">
      {/* Day header */}
      <div className="bg-gradient-to-r from-muted/40 via-muted/20 to-muted/40 dark:from-muted/30 dark:via-muted/10 dark:to-muted/30 px-5 py-4 border-b border-border/60">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center font-bold text-lg ${
            isToday(date)
              ? 'bg-primary text-primary-foreground shadow-soft-sm'
              : 'bg-muted text-foreground'
          }`}>
            {date.getDate()}
          </div>
          <div>
            <div className="text-sm font-bold text-foreground">
              {date.toLocaleDateString('en-US', { weekday: 'long' })}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
          </div>
          {dayEvents.length > 0 && (
            <span className="ml-auto text-xs font-semibold text-muted-foreground bg-muted px-2.5 py-1 rounded-lg">
              {dayEvents.length} event{dayEvents.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
      </div>

      <div className="divide-y divide-border/40">
        {hours.map((hour) => {
          const hourEvents = dayEvents.filter((e) => {
            const eventHour = new Date(e.start_time).getHours();
            return eventHour === hour;
          });
          const slotDate = new Date(date);
          slotDate.setHours(hour, 0, 0, 0);
          const isNowHour = showNowLine && hour === currentHour;

          return (
            <div
              key={hour}
              className={`flex min-h-[56px] group/slot cursor-pointer transition-colors relative
                ${isNowHour ? 'bg-primary/[0.03] dark:bg-primary/[0.06]' : 'hover:bg-muted/30 dark:hover:bg-muted/10'}
              `}
              onClick={() => onTimeSlotClick(slotDate)}
            >
              {/* Now indicator line */}
              {isNowHour && (
                <div className="absolute left-16 right-0 top-0 h-[2px] bg-primary/60 z-10">
                  <div className="absolute -left-1 -top-[3px] w-2 h-2 rounded-full bg-primary" />
                </div>
              )}

              <div className="w-18 flex-shrink-0 py-2.5 px-3 text-xs text-muted-foreground/70 text-right border-r border-border/40 font-medium">
                {hour === 0 ? '12 AM' : hour < 12 ? `${hour} AM` : hour === 12 ? '12 PM' : `${hour - 12} PM`}
              </div>
              <div className="flex-1 py-1.5 px-3 space-y-1">
                {hourEvents.map((event) => (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2.5 rounded-xl px-3.5 py-2 cursor-pointer transition-all duration-200 hover:shadow-soft-sm active:scale-[0.99] border border-transparent hover:border-border/40"
                    style={{ backgroundColor: `${event.color || DEFAULT_EVENT_COLOR}12` }}
                    onClick={(e) => {
                      e.stopPropagation();
                      onEventClick(event);
                    }}
                  >
                    <span
                      className="w-2.5 h-2.5 rounded-md flex-shrink-0"
                      style={{ backgroundColor: event.color || DEFAULT_EVENT_COLOR }}
                    />
                    <span className="text-sm font-semibold truncate text-foreground">{event.title}</span>
                    <span className="text-xs text-muted-foreground ml-auto flex-shrink-0 font-medium">
                      {formatTimeRange(event.start_time, event.end_time)}
                    </span>
                  </motion.div>
                ))}
                {hourEvents.length === 0 && (
                  <div className="flex items-center justify-center h-full opacity-0 group-hover/slot:opacity-100 transition-opacity">
                    <Plus className="w-3.5 h-3.5 text-muted-foreground/25" />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const CalendarGrid: React.FC<CalendarGridProps> = ({ events, onDayClick, onEventClick, onCreateEvent }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [direction, setDirection] = useState(0);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  const monthDays = useMemo(() => getMonthDays(year, month), [year, month]);
  const weekDays = useMemo(() => getWeekDays(currentDate), [currentDate]);

  const navigate = (dir: number) => {
    setDirection(dir);
    const next = new Date(currentDate);
    if (viewMode === 'month') {
      next.setMonth(next.getMonth() + dir);
    } else if (viewMode === 'week') {
      next.setDate(next.getDate() + dir * 7);
    } else {
      next.setDate(next.getDate() + dir);
    }
    setCurrentDate(next);
  };

  const goToToday = () => {
    setDirection(0);
    setCurrentDate(new Date());
  };

  const headerLabel = useMemo(() => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    if (viewMode === 'week') {
      const start = weekDays[0];
      const end = weekDays[6];
      if (start.getMonth() === end.getMonth()) {
        return `${start.toLocaleDateString('en-US', { month: 'long' })} ${start.getDate()}–${end.getDate()}, ${start.getFullYear()}`;
      }
      return `${start.toLocaleDateString('en-US', { month: 'short' })} ${start.getDate()} – ${end.toLocaleDateString('en-US', { month: 'short' })} ${end.getDate()}, ${end.getFullYear()}`;
    }
    return currentDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }, [currentDate, viewMode, weekDays]);

  const todayEvents = useMemo(() => getEventsForDay(events, new Date()), [events]);

  return (
    <div className="space-y-5">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        {/* Navigation */}
        <div className="flex items-center gap-1.5 flex-1">
          <div className="flex items-center bg-muted/50 dark:bg-muted/30 rounded-xl p-0.5">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg hover:bg-card dark:hover:bg-card/60 transition-all text-muted-foreground hover:text-foreground active:scale-90"
              aria-label="Previous"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate(1)}
              className="p-2 rounded-lg hover:bg-card dark:hover:bg-card/60 transition-all text-muted-foreground hover:text-foreground active:scale-90"
              aria-label="Next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
          <h2 className="text-lg font-bold text-foreground m-0 ml-2 tracking-tight">{headerLabel}</h2>
        </div>

        {/* Actions */}
        <div className="flex items-center gap-2.5">
          <button
            onClick={goToToday}
            className="px-3.5 py-2 text-xs font-semibold rounded-xl border border-border/80 hover:bg-muted/60 dark:hover:bg-muted/30 transition-all text-foreground relative active:scale-[0.97]"
          >
            <span className="flex items-center gap-1.5">
              <CalendarDays className="w-3.5 h-3.5 text-muted-foreground" />
              Today
            </span>
            {todayEvents.length > 0 && (
              <span className="absolute -top-1.5 -right-1.5 w-4.5 h-4.5 min-w-[18px] rounded-full bg-primary text-primary-foreground text-2xs flex items-center justify-center font-bold shadow-soft-sm ring-2 ring-card">
                {todayEvents.length}
              </span>
            )}
          </button>

          {/* View mode toggle */}
          <div className="flex items-center bg-muted/50 dark:bg-muted/30 rounded-xl p-0.5 border border-border/40">
            {(['month', 'week', 'day'] as ViewMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3.5 py-2 text-xs font-semibold rounded-lg transition-all duration-250 capitalize ${
                  viewMode === mode
                    ? 'bg-card dark:bg-card/80 text-foreground shadow-soft-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {mode}
              </button>
            ))}
          </div>

          <button onClick={onCreateEvent} className="btn btn-primary text-xs px-4 py-2">
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Event
          </button>
        </div>
      </div>

      {/* Calendar body */}
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`${viewMode}-${currentDate.toISOString()}`}
          initial={{ opacity: 0, x: direction * 24 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: direction * -24 }}
          transition={{ duration: 0.22, ease: [0.23, 1, 0.32, 1] }}
        >
          {viewMode === 'month' && (
            <div className="border border-border/60 rounded-2xl overflow-hidden bg-card shadow-soft-sm">
              {/* Weekday headers */}
              <div className="grid grid-cols-7 border-b border-border/60 bg-gradient-to-b from-muted/50 to-muted/30 dark:from-muted/30 dark:to-muted/15">
                {WEEKDAYS.map((day, i) => (
                  <div key={day} className="py-3 text-center text-2xs font-bold text-muted-foreground uppercase tracking-widest border-r border-border/40 last:border-r-0">
                    <span className="hidden sm:inline">{day}</span>
                    <span className="sm:hidden">{WEEKDAYS_SHORT[i]}</span>
                  </div>
                ))}
              </div>

              {/* Day cells grid */}
              <div className="grid grid-cols-7">
                {monthDays.map((date, i) => (
                  <DayCell
                    key={i}
                    date={date}
                    currentMonth={month}
                    events={events}
                    onDayClick={onDayClick}
                    onEventClick={onEventClick}
                  />
                ))}
              </div>
            </div>
          )}

          {viewMode === 'week' && (
            <div className="border border-border/60 rounded-2xl overflow-hidden bg-card shadow-soft-sm">
              <div className="flex divide-x divide-border/40">
                {weekDays.map((date) => (
                  <WeekDayColumn
                    key={date.toISOString()}
                    date={date}
                    events={events}
                    onDayClick={onDayClick}
                    onEventClick={onEventClick}
                  />
                ))}
              </div>
            </div>
          )}

          {viewMode === 'day' && (
            <DayView
              date={currentDate}
              events={events}
              onEventClick={onEventClick}
              onTimeSlotClick={onDayClick}
            />
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default CalendarGrid;
